import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Download, Printer, Eye, Trash2, Calendar, User, Truck, X, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SavedChallans = ({ onClose, currentUser }) => {
  const [challans, setChallans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    party_name: '',
    challan_number: ''
  });
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0
  });

  const fetchChallans = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.party_name) params.append('party_name', filters.party_name);
      if (filters.challan_number) params.append('challan_number', filters.challan_number);

      const response = await axios.get(`${API}/api/challans?${params.toString()}`, { withCredentials: true });
      setChallans(response.data.challans || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 0
      }));
    } catch (error) {
      console.error("Failed to fetch challans:", error);
      toast.error("Failed to load saved challans");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchChallans();
  }, [fetchChallans]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchChallans();
  };

  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      party_name: '',
      challan_number: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewChallan = async (challanId) => {
    try {
      const response = await axios.get(`${API}/api/challans/${challanId}`, { withCredentials: true });
      setSelectedChallan(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error("Failed to load challan details");
    }
  };

  const handleDeleteChallan = async (challanId, challanNumber) => {
    if (!window.confirm(`Are you sure you want to delete challan ${challanNumber}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/challans/${challanId}`, { withCredentials: true });
      toast.success(`Challan ${challanNumber} deleted successfully`);
      fetchChallans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete challan");
    }
  };

  const handlePrintChallan = (challan) => {
    setSelectedChallan(challan);
    setShowDetailModal(true);
    // After modal opens, trigger print
    setTimeout(() => window.print(), 500);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-700" />
              Saved Challans
            </CardTitle>
            <CardDescription>View and manage all saved delivery challans</CardDescription>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <form onSubmit={handleSearch} className="p-4 bg-stone-50 rounded-lg border border-stone-200">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-stone-500" />
              <span className="font-medium text-stone-700">Filters</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs">Challan Number</Label>
                <Input
                  placeholder="Search..."
                  value={filters.challan_number}
                  onChange={(e) => setFilters({ ...filters, challan_number: e.target.value })}
                  className="mt-1"
                  data-testid="filter-challan-number"
                />
              </div>
              <div>
                <Label className="text-xs">Party Name</Label>
                <Input
                  placeholder="Search party..."
                  value={filters.party_name}
                  onChange={(e) => setFilters({ ...filters, party_name: e.target.value })}
                  className="mt-1"
                  data-testid="filter-party-name"
                />
              </div>
              <div>
                <Label className="text-xs">From Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                  className="mt-1"
                  data-testid="filter-start-date"
                />
              </div>
              <div>
                <Label className="text-xs">To Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                  className="mt-1"
                  data-testid="filter-end-date"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button type="submit" size="sm" className="bg-lime-700 hover:bg-lime-800">
                <Search className="w-3 h-3 mr-1" /> Search
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </form>

          {/* Challans Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead>Challan No.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Party Name</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead className="text-left">Total Qty (kg)</TableHead>
                  <TableHead className="text-left">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                      Loading challans...
                    </TableCell>
                  </TableRow>
                ) : challans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                      No saved challans found. Challans will appear here when you save them during dispatch.
                    </TableCell>
                  </TableRow>
                ) : (
                  challans.map((challan) => (
                    <TableRow key={challan.id} data-testid={`challan-row-${challan.id}`}>
                      <TableCell>
                        <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {challan.challan_number}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(challan.challan_date)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-3 h-3 text-stone-400" />
                          <span className="font-medium">{challan.to_name}</span>
                        </div>
                        {challan.to_phone && (
                          <div className="text-xs text-stone-500 mt-1">
                            Ph: {challan.to_phone}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {challan.vehicle_number ? (
                          <div className="flex items-center gap-1">
                            <Truck className="w-3 h-3 text-stone-400" />
                            <span className="text-sm">{challan.vehicle_number}</span>
                          </div>
                        ) : (
                          <span className="text-stone-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-left font-semibold text-lime-700">
                        {challan.total_quantity?.toFixed(2) || '0.00'}
                      </TableCell>
                      <TableCell className="text-left">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewChallan(challan.id)}
                            title="View Details"
                            data-testid={`view-challan-${challan.id}`}
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintChallan(challan)}
                            title="Print"
                            data-testid={`print-saved-challan-${challan.id}`}
                          >
                            <Printer className="w-4 h-4 text-lime-600" />
                          </Button>
                          {currentUser?.role === 'admin' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteChallan(challan.id, challan.challan_number)}
                              title="Delete"
                              data-testid={`delete-challan-${challan.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-stone-500">
                Showing {challans.length} of {pagination.total} challans
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 bg-stone-100 rounded text-sm">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Challan Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-700" />
              Challan Details: {selectedChallan?.challan_number}
            </DialogTitle>
            <DialogDescription>
              Created on {selectedChallan && formatDate(selectedChallan.challan_date)}
            </DialogDescription>
          </DialogHeader>

          {selectedChallan && (
            <div className="space-y-6 mt-4 print-challan-detail">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-lime-50 rounded-lg border border-lime-200">
                  <h4 className="font-semibold text-lime-800 mb-2">FROM</h4>
                  <p className="font-medium">{selectedChallan.from_warehouse}</p>
                  <p className="text-sm text-stone-600">{selectedChallan.from_location}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">TO</h4>
                  <p className="font-medium">{selectedChallan.to_name}</p>
                  <p className="text-sm text-stone-600">{selectedChallan.to_address || '-'}</p>
                  <p className="text-sm text-stone-600">Ph: {selectedChallan.to_phone || '-'}</p>
                  {selectedChallan.to_gst && (
                    <p className="text-xs text-stone-500 mt-1">GSTIN: {selectedChallan.to_gst}</p>
                  )}
                </div>
              </div>

              {/* Transport Info */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <h4 className="font-semibold text-stone-700 mb-2">Transport Details</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-stone-500">Vehicle:</span>
                    <span className="font-medium ml-2">{selectedChallan.vehicle_number || '-'}</span>
                  </div>
                  <div>
                    <span className="text-stone-500">Driver:</span>
                    <span className="font-medium ml-2">{selectedChallan.driver_name || '-'}</span>
                  </div>
                  <div>
                    <span className="text-stone-500">Driver Ph:</span>
                    <span className="font-medium ml-2">{selectedChallan.driver_phone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div>
                <h4 className="font-semibold text-stone-700 mb-2">Items ({selectedChallan.items?.length || 0})</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-left">Quantity (kg)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedChallan.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.product_name}</TableCell>
                        <TableCell>{item.category || '-'}</TableCell>
                        <TableCell className="text-left">{item.quantity?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-lime-50 font-semibold">
                      <TableCell colSpan={3} className="text-left">Total:</TableCell>
                      <TableCell className="text-left text-lime-700">
                        {selectedChallan.total_quantity?.toFixed(2)} kg
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end no-print">
                <Button
                  onClick={() => window.print()}
                  className="bg-lime-700 hover:bg-lime-800"
                >
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-challan-detail, .print-challan-detail * {
            visibility: visible;
          }
          .print-challan-detail {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SavedChallans;
